import { Icon, Typography } from "@/components/atoms";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { isUnvalidatedLocation } from "@/redux/slices/clientBooking/bookingPayload";
import { clsx } from "@/utils";

const poiTitle = (poi: PointsOfInterest) => poi.rm_label?.trim() || poi.address?.split(",")[0]?.trim() || poi.address;

/**
 * The trip's route as a vertical dotted timeline — pickup (primary dot) → any stops → destination
 * (orange dot). Shared by both Booking Summary variants so the Address box looks identical on the
 * draft steps and the priced Payment step. Type matches the Trip Details rows: 12px semibold label
 * over a 12px regular gray address.
 *
 * `pickupFlightNumber` is a PROP rather than read off the booking slice, because this renders on the
 * confirmation page too — where the draft has been cleared and the flight has to come from the
 * created booking instead. Shown under the FIRST location: the flight belongs to the pickup, and on a
 * trip that merely passes an airport it would be wrong anywhere else.
 */
export const RouteTimeline = ({
  locations,
  pickupFlightNumber,
  jobVerified,
}: {
  locations: PointsOfInterest[];
  pickupFlightNumber?: string | null;
  /** The booking's stored validation flag — a prop for the same reason as the flight number. */
  jobVerified?: boolean;
}) => (
  <div className="flex flex-col">
    {locations.map((poi, index) => {
      const isLast = index === locations.length - 1;
      return (
        <div key={`${poi.uuid || poi.address}-${index}`} className="flex gap-2.5">
          {/* Dot + dashed connector down to the next location */}
          <div className="flex flex-col items-center pt-1">
            <span className={clsx("h-2.5 w-2.5 shrink-0 rounded-full", isLast ? "bg-[#F79009]" : "bg-primary")} />
            {!isLast && <span className="my-1 w-px flex-1 border-l border-dashed border-neutral-mid-gray" />}
          </div>
          <div className={clsx("min-w-0", !isLast && "pb-4")}>
            <Typography variant="small" className="block">
              {poiTitle(poi)}
            </Typography>
            <Typography variant="small-regular" className="block text-neutral-dark-gray">
              {poi.address}
            </Typography>
            {index === 0 && pickupFlightNumber && (
              <Typography variant="small" className="block text-neutral-silver-gray">
                Flight: {pickupFlightNumber}
              </Typography>
            )}
            {/* Same wording as the bookings table's red flag, so the two surfaces agree. Before this
                the table warned and the sheet said nothing — it just showed a map of the wrong city
                and a $0.00 price, which reads as working. */}
            {isUnvalidatedLocation(poi, jobVerified) && (
              <Typography variant="small" className="flex items-center text-danger">
                Requires Validation
                <Icon name="Danger" size={14} variant="Bold" className="ml-1 inline text-warning" />
              </Typography>
            )}
          </div>
        </div>
      );
    })}
  </div>
);
